// pages/SegmentActivation.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import endpoints from "../config";
import moment from "moment"; // format created_at
import PreviewPanel from "../components/DatatablePreviewPanel";
import ConfirmModal from "../components/ConfirmModal";

const SegmentActivation = () => {
  const { authToken, logout } = useAuth();
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters & pagination
  const [filterName, setFilterName] = useState("");
  const [filterCreator, setFilterCreator] = useState("");
  const [filterDescription, setFilterDescription] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const headers = useMemo(() => ({ Authorization: `Bearer ${authToken}` }), [authToken]);

  // Preview modal state
  const [previewData, setPreviewData] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPagination, setPreviewPagination] = useState({ page: 1, limit: 500000, total: 0 });
  const [previewSegment, setPreviewSegment] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);

  // Delete confirm modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toDeleteId, setToDeleteId] = useState(null);

  // Offcanvas filters (mobile)
  const [showFiltersOffcanvas, setShowFiltersOffcanvas] = useState(false);

  const fetchSegments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(endpoints.activation.segmentList, { headers });
      // Expecting res.data to be array
      setSegments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error("Failed to fetch segment list");
    } finally {
      setLoading(false);
    }
  }, [headers, logout]);

  const toggleStatus = async (segmentId, currentStatus) => {
    try {
      await axios.post(
        endpoints.activation.segmentStatusUpdate,
        { uuid: segmentId, status: !currentStatus },
        { headers }
      );
      toast.success("Status updated!");
      await fetchSegments();

      // If the segment was active and is now inactive, close the preview modal
      if (showPreviewModal && previewSegment && previewSegment.uuid === segmentId && currentStatus) {
        setShowPreviewModal(false);
        setPreviewSegment(null);
        setPreviewData(null);
      }
    } catch {
      toast.error("Failed to update status.");
    }
  };

  const togglePlatform = async (uuid, platformKey, value) => {
    try {
      await axios.post(
        endpoints.activation.segmentUpdateDestination,
        { uuid, [platformKey]: value },
        { headers }
      );
      toast.success(`${platformKey.replace("_", " ")} updated!`);
      await fetchSegments();
    } catch {
      toast.error("Failed to update destination.");
    }
  };

  // Preview API call
  const handlePreview = async (segment, pageNumber = 1) => {
    setPreviewLoadingId(segment?.uuid);
    try {
      const queryValue = segment?.uuid || "";
      if (!queryValue) {
        toast.error("No query found for this segment.");
        setPreviewLoadingId(null);
        return;
      }
      const payload = {
        uuid: queryValue,
        limit: previewPagination.limit,
        page: pageNumber,
      };
      const res = await axios.post(endpoints.queries.advanceGroupDirectPreview, payload, { headers });
      setPreviewData(res.data);
      setPreviewPagination(prev => ({ ...prev, page: pageNumber, total: res.data?.total || 0 }));
      setPreviewSegment({ ...segment });
      setShowPreviewModal(true);
    } catch (err) {
      toast.error("Failed to fetch preview data");
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const handleDeleteSegment = async () => {
    try {
      if (!toDeleteId) {
        toast.error("No segment selected");
        return;
      }
      await axios.delete(`${endpoints.queries.advanceDeleteSavedQuery}${toDeleteId}`, {
        headers,
      });
      toast.success("Segment deleted");
      setShowDeleteModal(false);
      setToDeleteId(null);
      // Remove the deleted row locally
      setSegments(prev => prev.filter(seg => seg.uuid !== toDeleteId && seg.squid !== toDeleteId));
    } catch {
      toast.error("Failed to delete segment");
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setToDeleteId(null);
  };

  useEffect(() => {
    fetchSegments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // fetch on mount

  const getBadge = (value, type = "type") => {
    if (type === "type") return value === "dynamic" ? "success" : "warning text-dark";
    if (type === "status") return value ? "success" : "danger";
    return "secondary";
  };

  // Filtering logic
  const filteredSegments = segments.filter(seg => {
    const name = (seg?.name || "").toString().toLowerCase();
    const creator = (seg?.creator_name || "Default").toString().toLowerCase();
    const description = (seg?.description || "--").toString().toLowerCase();
    const matchesName = name.includes(filterName.toLowerCase());
    const matchesCreator = creator.includes(filterCreator.toLowerCase());
    const matchesDescription = description.includes(filterDescription.toLowerCase());
    const matchesStatus =
      filterStatus === "" ||
      (filterStatus === "active" ? seg.status === true : seg.status === false);
    return matchesName && matchesCreator && matchesDescription && matchesStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filteredSegments.length / pageSize));
  // Clamp page within range
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const paginatedSegments = filteredSegments.slice((page - 1) * pageSize, page * pageSize);

  // Reset filters (used by offcanvas)
  const resetFilters = () => {
    setFilterName("");
    setFilterCreator("");
    setFilterDescription("");
    setFilterStatus("");
    setPage(1);
  };

  return (
    <div className="container-fluid mt-4">
      {loading ? (
        <div className="alert alert-info">Loading segments...</div>
      ) : (
        <>
          {/* Stats row */}
          <div className="row mb-4">
            <div className="col-12 col-md-6 mb-3 mb-md-0">
              <div className="card border-primary shadow-sm h-100">
                <div className="card-body">
                  <h5 className="card-title text-primary">📊 Total Segments</h5>
                  <h2 className="mb-0">{segments.length}</h2>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div className="card border-success shadow-sm h-100">
                <div className="card-body">
                  <h5 className="card-title text-success">✅ Active Segments</h5>
                  <h2 className="mb-0">{segments.filter(s => s.status).length}</h2>
                </div>
              </div>
            </div>
          </div>

          {/* Manager Card */}
          <div className="card shadow">
            <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center flex-wrap">
              <h5 className="mb-0">Segment Activation Manager</h5>

              {/* Mobile: Filters button */}
              <div className="d-block d-md-none mt-2 mt-md-0">
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => setShowFiltersOffcanvas(true)}
                >
                  <i className="fa fa-filter" aria-hidden="true" /> Filters
                </button>
              </div>
            </div>

            {/* Offcanvas Filters for small screens */}
            {showFiltersOffcanvas && (
              <>
                <div
                  className={`offcanvas offcanvas-start show d-block`}
                  tabIndex="-1"
                  style={{ visibility: "visible", width: "320px", backgroundColor: "var(--bs-body-bg)",zIndex:"99999",top:65 }}
                  aria-labelledby="offcanvasFiltersLabel"
                >
                  <div className="offcanvas-header">
                    <h5 id="offcanvasFiltersLabel">Filters</h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setShowFiltersOffcanvas(false)}
                    />
                  </div>
                  <div className="offcanvas-body">
                    <div className="mb-2">
                      <label className="form-label small mb-1">Name</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={filterName}
                        onChange={e => setFilterName(e.target.value)}
                        placeholder="Filter Name"
                      />
                    </div>

                    <div className="mb-2">
                      <label className="form-label small mb-1">Creator</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={filterCreator}
                        onChange={e => setFilterCreator(e.target.value)}
                        placeholder="Filter Creator"
                      />
                    </div>

                    <div className="mb-2">
                      <label className="form-label small mb-1">Description</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={filterDescription}
                        onChange={e => setFilterDescription(e.target.value)}
                        placeholder="Filter Description"
                      />
                    </div>

                    <div className="mb-2">
                      <label className="form-label small mb-1">Status</label>
                      <select
                        className="form-select form-select-sm"
                        value={filterStatus}
                        onChange={e => setFilterStatus(e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                    <div className="d-flex gap-2 mt-3">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                          setShowFiltersOffcanvas(false);
                          setPage(1);
                        }}
                      >
                        Apply
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          resetFilters();
                          setShowFiltersOffcanvas(false);
                        }}
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                </div>

                {/* Backdrop */}
                <div
                  className="modal-backdrop fade show"
                  onClick={() => setShowFiltersOffcanvas(false)}
                  style={{ cursor: "pointer" }}
                />
              </>
            )}

            <div className="card-body p-0">
              {/* Desktop Table (md+) */}
              <div className="table-responsive d-none d-md-block">
                <table className="table table-striped table-hover align-middle mb-0">
                  <thead className="table-light">
                    {/* Filter inputs row - visible only on md+ */}
                    <tr className="d-none d-md-table-row">
                      <th style={{ minWidth: "160px" }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Filter Name"
                          value={filterName}
                          onChange={e => { setFilterName(e.target.value); setPage(1); }}
                        />
                      </th>
                      <th></th>
                      <th style={{ minWidth: "150px" }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Filter Creator"
                          value={filterCreator}
                          onChange={e => { setFilterCreator(e.target.value); setPage(1); }}
                        />
                      </th>
                      <th style={{ minWidth: "220px" }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Filter Description"
                          value={filterDescription}
                          onChange={e => { setFilterDescription(e.target.value); setPage(1); }}
                        />
                      </th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th style={{ minWidth: "130px" }}>
                        <select
                          className="form-select form-select-sm"
                          value={filterStatus}
                          onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
                        >
                          <option value="">All</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </th>
                      <th></th>
                      <th></th>
                    </tr>

                    <tr>
                      <th style={{ maxWidth: "180px", whiteSpace: "normal", wordBreak: "break-word" }}>
                        Segment Name
                      </th>
                      <th>Type</th>
                      <th>Creator</th>
                      <th style={{ maxWidth: "240px", whiteSpace: "normal", wordBreak: "break-word" }}>
                        Description
                      </th>
                      <th>Created</th>
                      <th>Last Run At</th>
                      <th>Records</th>
                      <th>Status</th>
                      <th>Destinations</th>
                      <th>Preview / Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedSegments.map((seg, i) => (
                      <tr key={seg?.uuid || seg?.squid || i}>
                        <td style={{ maxWidth: "180px", whiteSpace: "normal", wordBreak: "break-word" }}>
                          <div className="text-truncate" style={{ maxWidth: "180px" }}>
                            {seg?.name || "--"}
                          </div>
                        </td>

                        <td>
                          <span className={`badge bg-${getBadge(seg?.type, "type")}`}>
                            {seg?.type || "--"}
                          </span>
                        </td>

                        <td>{seg?.creator_name || "Default"}</td>

                        <td style={{ minWidth: "200px", maxWidth: "300px" }}>
                          <textarea
                            id={`segmentDescription-${i}`}
                            className="form-control"
                            rows={2}
                            style={{ minHeight: "56px", maxHeight: "80px", resize: "none" }}
                            value={seg?.description || ""}
                            readOnly
                          />
                        </td>

                        <td>{seg?.created_at ? moment(seg.created_at).fromNow() : "--"}</td>
                        <td>{seg?.last_run_at ? moment(seg.last_run_at).fromNow() : "--"}</td>
                        <td>{seg?.records?.toLocaleString?.() || 0}</td>

                        <td>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className={`badge bg-${getBadge(seg?.status, "status")}`}>
                              {seg?.status ? "Active" : "Inactive"}
                            </span>
                            <div className="form-check form-switch m-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={!!seg?.status}
                                onChange={() => toggleStatus(seg?.uuid, seg?.status)}
                                aria-label={`Toggle status for ${seg?.name || "segment"}`}
                              />
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex align-items-center gap-2">
                              <label className="form-label m-0 small">Facebook</label>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={!!seg?.facebook_ads}
                                onChange={() => togglePlatform(seg?.uuid, "facebook_ads", !seg?.facebook_ads)}
                                aria-label={`Toggle Facebook destination for ${seg?.name || "segment"}`}
                              />
                            </div>

                            <div className="d-flex align-items-center gap-2">
                              <label className="form-label m-0 small">Google</label>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={!!seg?.google_ads}
                                onChange={() => togglePlatform(seg?.uuid, "google_ads", !seg?.google_ads)}
                                aria-label={`Toggle Google destination for ${seg?.name || "segment"}`}
                              />
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            {seg?.status ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-info"
                                onClick={() => handlePreview(seg)}
                                disabled={previewLoadingId === seg?.uuid}
                                title="Preview"
                              >
                                {previewLoadingId === seg?.uuid ? (
                                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                ) : (
                                  <i className="fas fa-eye" />
                                )}
                              </button>
                            ) : (
                              <button type="button" className="btn btn-sm btn-danger" disabled title="Preview">
                                <i className="fas fa-eye" />
                              </button>
                            )}

                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => {
                                setToDeleteId(seg?.squid || seg?.uuid);
                                setShowDeleteModal(true);
                              }}
                              title="Delete"
                            >
                              <i className="fas fa-trash-alt" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}

                    {paginatedSegments.length === 0 && (
                      <tr>
                        <td colSpan="10" className="text-center text-muted py-4">
                          No segments found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card List (sm, xs) */}
              <div className="d-block d-md-none">
                {paginatedSegments.length === 0 && (
                  <div className="p-3 text-center text-muted">No segments found.</div>
                )}

                {paginatedSegments.map((seg, i) => (
                  <div key={seg?.uuid || seg?.squid || i} className="card m-2">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1">{seg?.name || "--"}</h6>
                          <div className="mb-2">
                            <span className={`badge bg-${getBadge(seg?.type, "type")} me-2`}>
                              {seg?.type || "--"}
                            </span>
                            <small className="text-muted">By {seg?.creator_name || "Default"}</small>
                          </div>
                        </div>

                        <div className="text-end">
                          <div>
                            <span className={`badge bg-${getBadge(seg?.status, "status")}`}>
                              {seg?.status ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <div className="mt-2">
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={!!seg?.status}
                                onChange={() => toggleStatus(seg?.uuid, seg?.status)}
                                aria-label={`Toggle status for ${seg?.name || "segment"}`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="my-2">
                        <textarea
                          className="form-control"
                          rows={3}
                          style={{ minHeight: "60px", maxHeight: "120px", resize: "none" }}
                          value={seg?.description || ""}
                          readOnly
                        />
                      </div>

                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="small text-muted">
                          <div>Created: {seg?.created_at ? moment(seg.created_at).fromNow() : "--"}</div>
                          <div>Last run: {seg?.last_run_at ? moment(seg.last_run_at).fromNow() : "--"}</div>
                          <div>Records: {seg?.records?.toLocaleString?.() || 0}</div>
                        </div>

                        <div className="d-flex gap-2 align-items-center">
                          <div className="form-check form-switch d-flex align-items-center">
                            <label className="form-label me-2 mb-0 small">FB</label>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={!!seg?.facebook_ads}
                              onChange={() => togglePlatform(seg?.uuid, "facebook_ads", !seg?.facebook_ads)}
                            />
                          </div>

                          <div className="form-check form-switch d-flex align-items-center">
                            <label className="form-label me-2 mb-0 small">G</label>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={!!seg?.google_ads}
                              onChange={() => togglePlatform(seg?.uuid, "google_ads", !seg?.google_ads)}
                            />
                          </div>

                          {seg?.status ? (
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => handlePreview(seg)}
                              disabled={previewLoadingId === seg?.uuid}
                              title="Preview"
                            >
                              {previewLoadingId === seg?.uuid ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              ) : (
                                <i className="fas fa-eye" />
                              )}
                            </button>
                          ) : (
                            <button className="btn btn-sm btn-danger" disabled title="Preview">
                              <i className="fas fa-eye" />
                            </button>
                          )}

                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => {
                              setToDeleteId(seg?.squid || seg?.uuid);
                              setShowDeleteModal(true);
                            }}
                          >
                            <i className="fas fa-trash-alt" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="card-footer">
                <nav>
                  <ul className="pagination justify-content-center flex-wrap mb-0">
                    <li className={`page-item${page === 1 ? " disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => setPage(prev => Math.max(1, prev - 1))}
                        aria-label="Previous"
                      >
                        Previous
                      </button>
                    </li>

                    {Array.from({ length: totalPages }).map((_, idx) => {
                      const p = idx + 1;
                      // show behavior: if too many pages, show first, last and nearby pages
                      // but to keep it simple now, show all (pageSize is small)
                      return (
                        <li key={p} className={`page-item${page === p ? " active" : ""}`}>
                          <button className="page-link" onClick={() => setPage(p)}>
                            {p}
                          </button>
                        </li>
                      );
                    })}

                    <li className={`page-item${page === totalPages ? " disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                        aria-label="Next"
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <>
          <div
            className="modal show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h5 className="modal-title">
                    📊 Preview Result ({previewPagination?.total || 0} records)
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowPreviewModal(false)} />
                </div>
                <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                  <PreviewPanel
                    previewData={previewData}
                    pagination={previewPagination}
                    previewQuery={(pg) => handlePreview(previewSegment, pg)}
                    isSegment={true}
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowPreviewModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Backdrop */}
          <div className="modal-backdrop fade show" />
        </>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteModal && (
        <ConfirmModal
          show={showDeleteModal}
          onConfirm={handleDeleteSegment}
          onCancel={handleCancelDelete}
          title="Delete Segment"
          message="Are you sure you want to delete this segment?"
        />
      )}
    </div>
  );
};

export default SegmentActivation;
